import type { GMetrixTableCellProps, GMetrixTableColumnProps } from 'components/GMetrixTable/components';
import { useCustomTestsSearch } from 'pages/custom_tests_v1/components/custom_tests_search/context';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import type { CustomTestListItem } from 'pages/custom_tests_v1/types';
import { useCustomTests } from 'pages/custom_tests_v1/context';
import { GMetrixTable } from 'components';
import { Link } from 'react-router-dom';

type CustomTestRow = CustomTestListItem & { Id: number };

export const CustomTestsTable = props => {

    const { fetchTests, handleAssign, handleCloneConfirmation, handleDeleteConfirmation, handleToggleStatus, loading, tests } = useCustomTestsSearch();
    const { permissions } = useCustomTests();
    const { formatMessage } = useIntl();

    const StatusCell = (cellProps: GMetrixTableCellProps<CustomTestRow>) => (
        <td>
            {permissions.CanEdit ? (
                <button
                    onClick={async e => {

                        e.preventDefault();
                        const success = await handleToggleStatus(cellProps.dataItem.Id);
                        if (!success) return;

                        fetchTests();

                    }}
                    className={`badge border-0 ${cellProps.dataItem.IsActive ? 'bg-success' : 'bg-secondary'}`}
                    disabled={loading}
                    style={{ cursor: loading ? 'default' : 'pointer' }}
                    title={formatMessage({ id: 'click_to_toggle_status.16554403', defaultMessage: 'Click to toggle status' })}
                >
                    {cellProps.dataItem.IsActive ? <FormattedMessage defaultMessage="Active" id="active.b9616c88" /> : <FormattedMessage defaultMessage="Inactive" id="inactive.5fb6d21a" />}
                </button>
            ) : (
                <span className={`badge ${cellProps.dataItem.IsActive ? 'bg-success' : 'bg-secondary'}`}>
                    {cellProps.dataItem.IsActive ? <FormattedMessage defaultMessage="Active" id="active.b9616c88" /> : <FormattedMessage defaultMessage="Inactive" id="inactive.5fb6d21a" />}
                </span>
            )}
        </td>
    );

    const ScoreCell = (cellProps: GMetrixTableCellProps<CustomTestRow>) => (
        <td>
            {cellProps.dataItem.MinScore}
            %
        </td>
    );

    const DateCell = (cellProps: GMetrixTableCellProps<CustomTestRow>) => (
        <td>
            <FormattedDate value={new Date(cellProps.dataItem.DateCreate)} />
        </td>
    );

    const ActionsCell = (cellProps: GMetrixTableCellProps<CustomTestRow>) => (
        <td>
            <div className="d-flex justify-content-end">
                {permissions.CanAssign && (
                    <GMetrixButton
                        className="btn-sm me-1"
                        disabled={loading}
                        onClick={() => handleAssign(cellProps.dataItem.Id, cellProps.dataItem.Name)}
                        variant="outline-success"
                    >
                        {<FormattedMessage defaultMessage="Assign" id="assign.0dabb6f2" />}
                    </GMetrixButton>
                )}

                {permissions.CanEdit && (
                    <Link
                        className="btn btn-outline-primary btn-sm me-1"
                        disabled={loading}
                        to={`edit?testId=${cellProps.dataItem.Id}`}
                    >
                        {<FormattedMessage defaultMessage="Edit" id="edit.00978daf" />}
                    </Link>
                )}

                {permissions.CanClone && (
                    <GMetrixButton
                        className="btn-sm me-1"
                        disabled={loading}
                        onClick={() => handleCloneConfirmation(cellProps.dataItem)}
                        variant="outline-secondary"
                    >
                        {<FormattedMessage defaultMessage="Clone" id="clone.747ba0bb" />}
                    </GMetrixButton>
                )}

                {permissions.CanDelete && (
                    <GMetrixButton
                        className="btn-sm"
                        disabled={loading}
                        onClick={() => handleDeleteConfirmation(cellProps.dataItem)}
                        variant="outline-danger"
                    >
                        {<FormattedMessage defaultMessage="Delete" id="delete.2d32914e" />}
                    </GMetrixButton>
                )}
            </div>
        </td>
    );

    const columns: GMetrixTableColumnProps<CustomTestRow>[] = [
        { field: 'Name', title: formatMessage({ id: 'test_name.77ea159f', defaultMessage: 'Test Name' }) },
        { field: 'Mode', title: formatMessage({ id: 'mode.71e0dd61', defaultMessage: 'Mode', description: 'Formulario' }) },
        { field: 'CategoryName', title: formatMessage({ id: 'category.f324da36', defaultMessage: 'Category' }) },
        { cell: ScoreCell, field: 'MinScore', title: formatMessage({ id: 'score.6ed6415a', defaultMessage: 'Score' }) },
        { field: 'CreatedBy', title: formatMessage({ id: 'created_by.1ae66f0e', defaultMessage: 'Created By', description: 'Gridview header' }) },
        { cell: StatusCell, field: 'IsActive', title: formatMessage({ id: 'status.ad856ad7', defaultMessage: 'Status' }), width: '1%' },
        { cell: DateCell, field: 'DateCreate', title: formatMessage({ id: 'created.8d9f8791', defaultMessage: 'Created' }) },
        { cell: ActionsCell, title: '', width: '1%' }
    ];

    return (
        <GMetrixTable<CustomTestRow>
            columnData={columns}
            loading={loading}
            rowData={tests}
            tableName={formatMessage({ id: 'customtests.5176c72c', defaultMessage: 'CustomTests' })}
        />
    );

};